import React, { useEffect, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell
} from "recharts";
import {
    ShoppingBag, Utensils, ShoppingCart
} from "lucide-react";
import axios from "axios";

const Orders = ({ isMobile }) => {

    const [ordersData, setOrdersData] = useState({
        summary: { totalOrders: 0 },
        byTimeSlot: []
    });

    const [loading, setLoading] = useState(false);

    /* ---------------- API INTEGRATION ---------------- */
    useEffect(() => {
        const fetchOrders = async () => {
            const vendorId = localStorage.getItem("vendorId");
            const token = localStorage.getItem("authToken");

            if (!vendorId || !token) {
                console.error("❌ Missing vendorId or authToken");
                return;
            }

            // ✅ Dates (last 7 days)
            const toDate = new Date().toISOString().split("T")[0];
            const fromDate = new Date(
                Date.now() - 7 * 24 * 60 * 60 * 1000
            ).toISOString().split("T")[0];

            setLoading(true);

            try {
                const res = await axios.get(
                    "http://testing.maamaas.com:8080/food/api/orders/vendor/statistics/custom",
                    {
                        params: {
                            vendorId,
                            fromDate,
                            toDate
                        },
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );

                console.log("✅ Orders API Response:", res.data);

                const api = res.data?.data || res.data || {};

                const dailyStats = Array.isArray(api.dailyStats)
                    ? api.dailyStats
                    : [];

                const byTimeSlot = dailyStats.map((d, index) => ({
                    label: new Date(d.date).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short"
                    }),
                    orders: Number(d.orders ?? 0),
                    color: index % 2 === 0 ? "#f59e0b" : "#10b981"
                }));

                setOrdersData({
                    summary: {
                        totalOrders: Number(api.totalOrders ?? 0)
                    },
                    byTimeSlot
                });

            } catch (error) {
                console.error("❌ Orders API error:", error.response || error);
                setOrdersData({
                    summary: { totalOrders: 0 },
                    byTimeSlot: []
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    /* ---------------- SUMMARY CARDS ---------------- */
    const OrdersSummaryCards = () => {
        const orderTotal = ordersData.summary.totalOrders;

        const orderCards = [
            { title: 'Total Orders', value: orderTotal, icon: <ShoppingBag size={18} />, color: '#f59e0b' },
            { title: 'Dine-in', value: Math.round(orderTotal * 0.45), icon: <Utensils size={18} />, color: '#10b981' },
            { title: 'Takeaway', value: Math.round(orderTotal * 0.25), icon: <ShoppingBag size={18} />, color: '#3b82f6' },
            { title: 'Delivery', value: Math.round(orderTotal * 0.30), icon: <ShoppingCart size={18} />, color: '#ef4444' }
        ];

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
                gap: 12,
                marginBottom: 20
            }}>
                {orderCards.map((card, index) => (
                    <div key={index} style={{
                        background: '#f8fafc',
                        borderRadius: 14,
                        padding: 16,
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <p style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{card.title}</p>
                            <p style={{ fontSize: 22, fontWeight: 800 }}>{card.value}</p>
                        </div>
                        <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: `${card.color}20`,
                            color: card.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {card.icon}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    /* ---------------- CHART ---------------- */
    const OrdersAnalysis = () => {
        if (loading) return <div style={{ padding: 40 }}>Loading orders...</div>;
        if (!ordersData.byTimeSlot.length)
            return <div style={{ padding: 40 }}>No order data available</div>;

        return (
            <div style={{
                background: 'white',
                borderRadius: 24,
                padding: isMobile ? 20 : 28,
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)'
            }}>
                <h3 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, marginBottom: 16 }}>
                    Orders Analysis
                </h3>

                <div style={{ height: isMobile ? 280 : 360 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={ordersData.byTimeSlot}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="label" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="orders" radius={[8, 8, 0, 0]}>
                                {ordersData.byTimeSlot.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    return (
        <>
            <OrdersSummaryCards />
            <OrdersAnalysis />
        </>
    );
};

export default Orders;
